import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {AppStateService} from '../../services/app-state.service';
import {DataService} from '../../services/data.service';
import {Timereport} from '../interfaces';
import {AuthService} from '../../services/auth.service';
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';
import {TimereportListFilter, TimereportListTableService} from '../../services/timereport-list-table.service';
import {MatInput, MatInputModule} from '@angular/material/input';
import {TimereportListTableComponent} from '../timereport-list-table/timereport-list-table.component';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatTabsModule} from '@angular/material/tabs';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {AsyncPipe} from '@angular/common';
import {SendReportDialogComponent} from '../send-report-dialog/send-report-dialog.component';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';



@Component({
    selector: 'timereport-list',
    templateUrl: './timereport-list.component.html',
    standalone: true,
    host: {
        '[style.display]': '\'flex\'',
        '[style.flex-direction]': '\'column\'',
        '[style.flex-grow]': '1'
    },
    imports: [
        ReactiveFormsModule,
        TimereportListTableComponent,
        MatDatepickerModule,
        MatTabsModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        MatTooltipModule,
        AsyncPipe,
        MatFormFieldModule
    ],
    styleUrls: ['./timereport-list.component.scss']
})
export class TimereportListComponent implements OnInit, OnDestroy {
    loading = false;

    @ViewChild('reportFromDate', {read: MatInput}) reportFromDate;
    @ViewChild('reportToDate', {read: MatInput}) reportToDate;

    filterForm: FormGroup;

    public timereports: Timereport[] = [];
    public sendReportDisabled = false;
    public reportDateFromSubject = new BehaviorSubject<Date>(moment().subtract(1, 'year').toDate());
    public reportDateToSubject = new BehaviorSubject<Date>(moment().toDate());
    public canChangeUser;
    public _canDownloadReport: boolean;
    public selectedTab = 0;
    public performingQuickSerch$: Observable<boolean>;
    public performingSlowSearch$: Observable<boolean>;
    public slowListElements = 0;
    private unsubscribe$ = new Subject();

    constructor(
        private fb: FormBuilder,
        private dataService: DataService,
        private authService: AuthService,
        public dialog: MatDialog,
        public appState: AppStateService,
        private timereportListTableService: TimereportListTableService
    ) {
        this.performingQuickSerch$ = this.timereportListTableService.isLoading$('quick');
        this.performingSlowSearch$ = this.timereportListTableService.isLoading$('slow');

        this.authService.userHasPermission('app.timereport.anyone.write').pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            this.canChangeUser = value;
        });
    }

    private _sentDates = new Set([]);

    @Input() set sentDates(sd: Set<number>) {
        this._sentDates = sd;
    }

    ngOnInit() {
        const userEmail = (this.canChangeUser) ? null : this.authService.currentUser.email;
        const storedFilters = this.timereportListTableService.retrieveFiltersFromStorage(userEmail);

        this.filterForm = this.fb.group({
            filter: [storedFilters.filter || ''],
            fromDate: [storedFilters.fromDate || moment().subtract(180, 'days').toDate()],
            toDate: [storedFilters.toDate || moment().toDate()]
        });

        this.filterForm.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            takeUntil(this.unsubscribe$)
        ).subscribe(values => {
            this.storeFilters(values);
        });

        this.authService.userHasPermission('app.timereport.report.download')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((val: boolean) => {
                this._canDownloadReport = val;
            });

        this.storeFilters(this.filterForm.value);
    }

    storeFilters(values: any) {
        const userEmail = (this.canChangeUser) ? null : this.authService.currentUser.email;
        const currentSort = this.timereportListTableService.currentFilter;

        const filter: TimereportListFilter = {
            filter: values.filter.trim(),
            toDate: new Date(values.toDate),
            fromDate: new Date(values.fromDate),
            userEmail: userEmail,
            sortActive: currentSort ? currentSort.sortActive : 'date',
            sortDirection: currentSort ? currentSort.sortDirection : 'desc'
        };
        this.timereportListTableService.storeFilters(filter);
    }

    resetDatePicker() {
        const defaultFrom = moment().subtract(180, 'days').toDate();
        const defaultTo = moment().toDate();

        this.filterForm.patchValue({
            fromDate: defaultFrom,
            toDate: defaultTo
        });
    }

    sendDailyReport() {
        if (!this.authService.currentUser) {
            this.appState.showError({errorMessage: 'Current user not defined', errorCode: -1});
            return;
        }

        this.sendReportDisabled = true;
        const dialogRef = this.dialog.open(SendReportDialogComponent, {
            width: '550px',
            data: {
                sentDates: this._sentDates,
                canChangeUser: this.canChangeUser,
                user: this.authService.currentUser
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                this.sendReportDisabled = false;
                return;
            }
            this.dataService.sendDailyReport(result)
                .subscribe((data: any) => {
                    this.sendReportDisabled = false;
                    this.appState.infoDialog({
                        infoMessage: data.message ? data.message : 'Report sent',
                        infoHtmlDetail: data.emailBody
                    });
                }, (err) => {
                    this.sendReportDisabled = false;
                    this.appState.showError({errorMessage: err.message, errorCode: -1});
                });
        });
    }

    async downloadHourReport() {
        const from = this.reportFromDate.value.toDate();
        const to = this.reportToDate.value.toDate();
        console.log('Creating excel with.. ', from, to);

        try {
            this.loading = true;
            const reportData = await this.dataService.getTimereportsReport(from, to).toPromise();
            const wb = XLSX.utils.book_new();
            const sheetData1 = [reportData.firstSheetHeader];
            Object.keys(reportData.report1).map(key => {
                sheetData1.push([
                    key,
                    reportData.report1[key].category,
                    reportData.report1[key].hours
                ])
            });

            const ws1 = XLSX.utils.aoa_to_sheet(sheetData1);
            ws1['!cols'] = reportData.firstSheetHeader.map(i => ({wch: i.length}));
            XLSX.utils.book_append_sheet(wb, ws1, 'Overview');

            const sheetData2 = [reportData.secondSheetHeader];
            Object.keys(reportData.report2).forEach(nomeUtente => {
                const row: any[] = [nomeUtente];
                for (const key of reportData.secondSheetHeader) {
                    if (key === 'Nome Utente') continue;
                    reportData.report2[nomeUtente][key] ? row.push(reportData.report2[nomeUtente][key]) : row.push(0);
                }
                sheetData2.push(row);
            });

            const ws2 = XLSX.utils.aoa_to_sheet(sheetData2);
            XLSX.utils.book_append_sheet(wb, ws2, 'Ore utenti');
            ws2['!cols'] = reportData.secondSheetHeader.map(i => ({wch: i.length}));

            const fileName = `${this.reportFromDate.value.format('DDMMYYYY')}_${this.reportToDate.value.format('DDMMYYYY')}_centri_di_costo_export.xlsx`;
            XLSX.writeFile(wb, fileName);
            this.loading = false;
        } catch (err) {
            console.error(err);
            this.loading = false;
        }
    }

    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
